import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_BASE } from '../../constants/general.constants';

// Server response interface (from /api/integrations)
interface Integration {
  integrationId: number;
  name: string;
  displayName: string;
}

// Client interface (unchanged for existing components)
export interface Feature {
  featureId: number;
  name: string;
  displayName: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private http = inject(HttpClient);

  private readonly integrationsEndpoint = `${API_BASE}/integrations`;

  // ────────────────────────────────────────────────────────────────────────────────
  // Global Features Management

  /**
   * Get all available features in the system (mapped from integrations)
   * @returns Observable<Feature[]> List of all features
   */
  getFeatures(): Observable<Feature[]> {
    return this.http.get<Integration[]>(this.integrationsEndpoint).pipe(
      map(integrations => integrations.map(integration => ({
        featureId: integration.integrationId,  // Map integrationId to featureId
        name: integration.name,
        displayName: integration.displayName
      })))
    );
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Helper Methods

  /**
   * Check if a feature is enabled for a user based on their featureIds
   * @param userFeatureIds Array of feature IDs enabled for the user
   * @param featureId Feature ID to check
   * @returns boolean Whether the feature is enabled
   */
  isFeatureEnabled(userFeatureIds: number[], featureId: number): boolean {
    return userFeatureIds.includes(featureId);
  }

  /**
   * Check if a feature is enabled for a project
   * @param projectFeatureIds Array of feature IDs enabled for the project
   * @param featureId Feature ID to check
   * @returns boolean Whether the feature is enabled for the project
   */
  isProjectFeatureEnabled(projectFeatureIds: number[], featureId: number): boolean {
    return projectFeatureIds.includes(featureId);
  }

  /**
   * Get feature by ID from a list of features
   * @param features Array of features
   * @param featureId Feature ID to find
   * @returns Feature | undefined The found feature or undefined
   */
  getFeatureById(features: Feature[], featureId: number): Feature | undefined {
    return features.find((feature) => feature.featureId === featureId);
  }

  /**
   * Get feature by name from a list of features
   * @param features Array of features
   * @param featureName Feature name to find
   * @returns Feature | undefined The found feature or undefined
   */
  getFeatureByName(
    features: Feature[],
    featureName: string
  ): Feature | undefined {
    return features.find((feature) => feature.name === featureName);
  }
}
